
import ActionModal from "JS/components/modals/ActionModal.vue";
import AppointmentTypeRadioCard from "./AppointmentTypeRadioCard.vue";
import AutomotiveServiceCenterStoreInfo from "JS/automotive/shared/components/AutomotiveServiceCenterStoreInfo.vue";
import BButton from "JS/components/BButton.vue";
import BForm from "JS/components/blain-form/BForm.vue";
import BRadioGroup from "JS/components/blain-form/BRadioGroup.vue";
import ChangeStoreSideMenu from "JS/components/side-menus/change-store/ChangeStoreSideMenu.vue";
import DatePickerCalendar from "JS/components/date-picker/DatePickerCalendar.vue";
import FieldNote from 'JS/components/FieldNote.vue';
import FullScreenLoader from "JS/components/FullScreenLoader.vue";
import Icon from "JS/components/Icon.vue";
import StepControls from "../StepControls.vue";
import TimeSlotCalendar from "./TimeSlotCalendar.vue";
import UiButton from "JS/components/UiButton.vue";
import { addDays, eachDayOfInterval } from "date-fns/fp";
import { AutomotiveSchedulerStep, WorkOrderAppointmentInfoState, WorkOrderErrorType } from "JS/automotive/auto-service-scheduler/types/session";
import { computed, defineComponent, nextTick, ref, watch } from "vue";
import { createDateLookup, createTimeSlotLookup, DateLookup, TimeSlotLookup, timeSlotsAreEquivalent } from "JS/automotive/auto-service-scheduler/utils/calendar";
import { DateRange, deserializeDateRange, SerializedDateRange } from "JS/types/Generic";
import { delay } from "JS/utilities/promise";
import { format } from 'date-fns/fp';
import { IDropOffDatesResource, ITimeSlotsResource } from "JS/automotive/auto-service-scheduler/types/appointment";
import { StoreChangedEvent } from "JS/components/side-menus/change-store/change-store-types";
import { useBreakpoints } from "JS/composables/breakpoint";
import { useOrderStore } from "JS/automotive/auto-service-scheduler/state/order";
import { useReducer } from "JS/composables/reducer";
import { useServices } from "JS/automotive/auto-service-scheduler/services";

const defaultDropOffDates = createDateLookup(eachDayOfInterval({ start: new Date, end: addDays(30, new Date) }));
const defaultTimeSlots = createTimeSlotLookup([]);
const getDayAriaLabel = format('EEEE MMMM do, Y');
const formatTime =  format('h:mm a');

type AppointmentState = {
	type:  'unset' | 'dropoff' | 'timeslot';
	loading: boolean;
	availableDropoffDates: DateLookup;
	availableTimeSlots: TimeSlotLookup;
	dropoffDate: Date | null;
	timeSlot: DateRange | null;
}

type AppointmentAction =
	| { type: 'update-type', appointmentType: 'dropoff' | 'timeslot' }
	| { type: 'select-date', date: Date }
	| { type: 'select-timeslot', timeslot: DateRange }
	| { type: 'receive-dropoff-dates', dates: string[] }
	| { type: 'receive-timeslots', timeslots: SerializedDateRange[] }
	| { type: 'receive-error' }

function appointmentReducer(state: AppointmentState, action: AppointmentAction): AppointmentState {
	switch (action.type) {
		case 'update-type': {
			return {
				...state,
				loading: true,
				type: action.appointmentType,
				availableDropoffDates: defaultDropOffDates,
				availableTimeSlots: defaultTimeSlots,
				dropoffDate: null,
				timeSlot: null,
			};
		}
		case 'select-date': {
			return { ...state, dropoffDate: action.date };
		}
		case 'select-timeslot': {
			return { ...state, timeSlot: action.timeslot };
		}
		case 'receive-dropoff-dates': {
			return {
				...state,
				loading: false,
				availableDropoffDates: createDateLookup(action.dates),
			};
		}
		case 'receive-timeslots': {
			return {
				...state,
				loading: false,
				availableTimeSlots: createTimeSlotLookup(action.timeslots)
			};
		}
		case 'receive-error': {
			return {
				...state,
				loading: false,
			};
		}
		default:
			return state;
	}
}

export default defineComponent({
	name: 'SelectAppointmentStep',
	components: {
		AutomotiveServiceCenterStoreInfo,
		AppointmentTypeRadioCard,
		BButton,
		BForm,
		BRadioGroup,
		DatePickerCalendar,
		FullScreenLoader,
		StepControls,
		ChangeStoreSideMenu,
		TimeSlotCalendar,
		UiButton,
		FieldNote,
		ActionModal,
		Icon,
	},
	emits: ['previous'],
	setup() {
		const { state, dispatch } = useReducer(appointmentReducer, {
			loading: false,
			type: 'unset',
			availableDropoffDates: defaultDropOffDates,
			availableTimeSlots: defaultTimeSlots,
			dropoffDate: null,
			timeSlot: null,
		});

		const orderStore = useOrderStore();
		const serviceCenter = computed(() => orderStore.session?.appointmentInfo?.serviceCenter);
		const estimatedAppointmentDuration = computed(() => orderStore.session?.appointmentInfo?.estimatedAppointmentDurationLabel);

		const showReselectAppointmentModal = computed(() => orderStore.globalError?.type === WorkOrderErrorType.ReselectAppointment);
		const showChangeStore = ref(false);

		const dropoffDate = computed({
			get: () => state.value.dropoffDate,
			set: date => dispatch({ type: 'select-date', date }),
		});
		const availableDropoffDates = computed<DateLookup>(() => state.value.availableDropoffDates);

		const timeSlot = computed({
			get: () => state.value.timeSlot,
			set: timeslot => dispatch({ type: 'select-timeslot', timeslot })
		});
		const availableTimeSlots = computed(() => state.value.availableTimeSlots);

		const canContinue = computed(() => submissionType.value !== 'incomplete' && (dropoffDate.value != null || timeSlot.value != null));

		const { isMobile } = useBreakpoints();

		const appointmentSummaryString = computed(() => {
			let info = "selected appointment: ";
			if (state.value.type === 'dropoff' && dropoffDate.value) {
				info += "drop off; " + getDayAriaLabel(dropoffDate.value);
			} else if (state.value.type === 'timeslot' && timeSlot.value) {
				const start = new Date(timeSlot.value.start);
				info += getDayAriaLabel(start) + " at " + formatTime(start);
			} else {
				info += "none";
			}
			return info;
		});

		const { appointmentService, dispatchSchedulerEvent } = useServices();

		// used to make sure we only ever take action when we're looking at the latest request
		let lastPromise: Promise<IDropOffDatesResource | ITimeSlotsResource> | null = null;
		async function updateAppointmentData(dropOff: boolean) {
			if (dropOff == null || serviceCenter.value == null) {
				return;
			}

			const appointmentType = dropOff ? 'dropoff' : 'timeslot';
			dispatch({ type: 'update-type', appointmentType });

			const promise = delay<IDropOffDatesResource | ITimeSlotsResource>(
				dropOff ? appointmentService.getAvailableDropoffDates() : appointmentService.getAvailableTimeSlots(),
				750
			);
			lastPromise = promise;

			const res = await promise;

			if (promise !== lastPromise) {
				return;
			}

			if (res.errorMessage) {
				orderStore.setGlobalErrorMessage(res.errorMessage);
				dispatch({ type: 'receive-error' });
				return;
			}

			const { appointmentInfo } = orderStore.session!;
			if ('availableDropoffDates' in res) {
				dispatchSchedulerEvent({
					event: 'scheduler_schedule_dropoff',
					availableOptions: res.availableDropoffDates.length
				});
				dispatch({ type: 'receive-dropoff-dates', dates: res.availableDropoffDates });

				if (appointmentInfo.state === WorkOrderAppointmentInfoState.Dropoff && appointmentInfo.selectedDate) {
					dispatch({ type: 'select-date', date: new Date(appointmentInfo.selectedDate) });
				}

			} else {
				dispatchSchedulerEvent({
					event: 'scheduler_schedule_timeslot',
					availableOptions: res.availableTimeSlots.length
				});
				dispatch({ type: 'receive-timeslots', timeslots: res.availableTimeSlots });

				if (appointmentInfo.state === WorkOrderAppointmentInfoState.TimeSlot && appointmentInfo.selectedTimeSlot) {
					const selectedTimeSlot = deserializeDateRange(appointmentInfo.selectedTimeSlot);
					const timeSlotsOnDay = state.value.availableTimeSlots.getAllForDay(selectedTimeSlot.start);
					const slot = timeSlotsOnDay.find(s => timeSlotsAreEquivalent(s, selectedTimeSlot));
					if (slot) {
						dispatch({ type: 'select-timeslot', timeslot: slot });
					}
				}

			}
		}

		function logAppointmentData(res: IDropOffDatesResource | ITimeSlotsResource) {
			if (res.errorMessage) {
				return;
			}

			if ('availableDropoffDates' in res) {
				dispatchSchedulerEvent({ event: 'scheduler_schedule_dropoff', availableOptions: res.availableDropoffDates.length });
			} else {
				dispatchSchedulerEvent({ event: 'scheduler_schedule_timeslot', availableOptions: res.availableTimeSlots.length });
			}
		}

		const stopWatchingAppointmentState = watch(() => orderStore?.session?.appointmentInfo, appointmentInfo => {
			if (!appointmentInfo || !appointmentInfo.serviceCenter) {
				return;
			}
			switch (appointmentInfo.state) {
				case WorkOrderAppointmentInfoState.Dropoff:
					updateAppointmentData(true);
					break;
				case WorkOrderAppointmentInfoState.TimeSlot:
					updateAppointmentData(false);
					break;
				case WorkOrderAppointmentInfoState.Empty:
					break;
				default:
					return;
			}

			nextTick(() => stopWatchingAppointmentState());
		}, {
			immediate: true,
		});

		const submissionType = computed<'dropoff' | 'time-slot' | 'incomplete'>(() => {
			if (!serviceCenter.value) {
				return 'incomplete';
			}

			if (state.value.type === 'dropoff' && dropoffDate.value) {
				return 'dropoff';
			}

			if (state.value.type === 'timeslot' && timeSlot.value) {
				return 'time-slot';
			}

			return 'incomplete';
		});

		async function handleSubmit() {
			await orderStore.submitAppointmentInfo({
				submissionType: submissionType.value,
				dropoffDate: dropoffDate.value,
				timeSlot: timeSlot.value
			});
		}

		return {
			updateAppointmentData,
			state,
			orderStore,
			serviceCenter,
			isMobile,
			dropoffDate,
			availableDropoffDates,
			timeSlot,
			availableTimeSlots,
			step: AutomotiveSchedulerStep.Appointment,
			handleSubmit,
			submissionType,
			showChangeStore,
			showReselectAppointmentModal,
			appointmentSummaryString,
			async selectNewAppointment() {
				orderStore.clearGlobalError();
				await orderStore.fetchSession();
				if (state.value.type !== 'unset') {
					await updateAppointmentData(state.value.type === 'dropoff');
				}
			},
			async handleStoreChanged(e: StoreChangedEvent) {
				e.preventDefault();
				showChangeStore.value = false;
				await orderStore.fetchSession();
				if (state.value.type !== 'unset') {
					await updateAppointmentData(state.value.type === 'dropoff');
				}
			},
			canContinue,
			estimatedAppointmentDuration
		};
	}
});
